import { Clipboard } from '@angular/cdk/clipboard';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FileUploadComponent,
  UploadStyles,
} from 'src/app/private/shared/components/file-management/file-upload/file-upload.component';
import { UploadType } from 'src/app/private/shared/enums/upload-type.enum';
import { FileDTO } from '../../dtos/file.dto';
import {
  AIUserFeedback,
  ChatMessage,
  Conversation,
  GlaiUserFeedbackPayload,
} from '../../dtos/glai.dto';
import { GlaiService } from '../../services/glai/glai.service';

@Component({
  selector: 'app-glai',
  templateUrl: './glai.component.html',
  styleUrl: './glai.component.scss',
  standalone: false,
})
export class GlaiComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  ratedThumbsUp = false;

  ratedThumbsDown = false;

  @Output() readonly fileUploads = new EventEmitter<FileDTO>();

  uploadedFiles: FileDTO[] = [];

  uploadStyle = UploadStyles.ICON;

  uploadType = UploadType.USER_UPLOAD;

  activeModes: string[] = ['default'];

  userMessage = '';

  messages: ChatMessage[] = [];

  currentConversation: Conversation | null = null;

  isLoading = false;

  error: string | null = null;

  private messageSubscription?: Subscription;

  private shouldScrollToBottom = false;

  selectedPath = 'Question & Answer';

  customCollapsed = false;

  AIUserFeedback = AIUserFeedback;

  constructor(private glaiService: GlaiService, private clipboard: Clipboard) {}

  ngOnInit(): void {
    this.messageSubscription = this.glaiService.messages$.subscribe(
      (message: ChatMessage) => {
        const existingIndex = this.messages.findIndex(
          (m) => m.id === message.id
        );
        if (existingIndex >= 0) {
          this.messages[existingIndex] = message;
        } else {
          this.messages.push(message);
        }
        this.shouldScrollToBottom = true;
      }
    );
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.messageSubscription?.unsubscribe();
    this.glaiService.disconnectStream();
  }

  toggleMode(mode: string): void {
    /* const index = this.activeModes.indexOf(mode);
    if (index > -1) {
      this.activeModes.splice(index, 1);
    } else {
      this.activeModes.push(mode);
    } */

    switch (mode) {
      case 'default':
        this.selectedPath = 'Question & Answer';
        break;

      case 'lesson_planning':
        this.selectedPath = 'Lesson & Activity Planning';
        break;

      case 'diff_and_mods':
        this.selectedPath = 'Differentiation and Modifications';
        break;

      case 'assessments_and_feedback':
        this.selectedPath = 'Assessments and Feedback';
        break;

      case 'communication':
        this.selectedPath = 'Professional Communication';
        break;

      default:
        this.selectedPath = 'Question & Answer';
        break;
    }

    this.activeModes = [mode];
  }

  sendMessage(): void {
    if (!this.userMessage.trim() || this.isLoading) return;

    const message = this.userMessage.trim();
    this.userMessage = '';
    this.isLoading = true;

    // Add user message to the chat
    const messageFiles = [...this.uploadedFiles];
    this.glaiService.addUserMessage(message, messageFiles);

    if (!this.currentConversation) {
      this.startNewConversation(message, messageFiles);
    } else {
      this.continueConversation(message, messageFiles);
    }

    // remove files
    this.uploadedFiles = [];
  }

  private startNewConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    this.glaiService
      .startConversation(message, messageFiles, this.activeModes)
      .subscribe({
        next: (response) => {
          this.currentConversation = response.item;
          this.glaiService.connectToStream(response.item.glai_stream_id);
          this.isLoading = false;
        },
        error: (error) => {
          this.error = `Failed to start conversation: ${error}`;
          this.isLoading = false;
        },
      });
  }

  private continueConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    if (!this.currentConversation) return;

    this.glaiService
      .continueConversation(
        this.currentConversation.id,
        message,
        messageFiles,
        this.activeModes
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.error = `Failed to continue conversation: ${error}`;
          this.isLoading = false;
        },
      });
  }

  addToFileList(files: FileDTO): void {
    // add to uploadedFileIds
    this.uploadedFiles.push(files);
  }

  removeFromFileList(file: FileDTO) {
    // remove from uploadedFiles
    this.uploadedFiles = this.uploadedFiles.filter((f) => f.id !== file.id);
  }

  private scrollToBottom(): void {
    this.messagesContainer.nativeElement.scrollTop =
      this.messagesContainer.nativeElement.scrollHeight;
  }

  toggleCustomCollapsed(): void {
    this.customCollapsed = !this.customCollapsed;
  }

  // BEGIN FACILITATOR ONLY FEATURES
  copyMessage(code: string, $event: Event) {
    this.clipboard.copy(code);
    const target = $event.target as HTMLInputElement | null;
    target?.classList.remove('bi-copy');
    target?.classList.add('bi-check2');
    setTimeout(() => {
      target?.classList.remove('bi-check2');
      target?.classList.add('bi-copy');
    }, 2000);
  }

  submitUserFeedback(message: ChatMessage, feedback: AIUserFeedback) {
    if (this.currentConversation) {
      const payload: GlaiUserFeedbackPayload = {
        user_feedback: feedback,
        message_text: message.content,
        role: 'assistant',
      };
      this.glaiService
        .submitUserFeedback(this.currentConversation.id, payload)
        .subscribe({
          next: () => {
            if (feedback === AIUserFeedback.THUMBS_UP) {
              this.ratedThumbsUp = true;
              this.ratedThumbsDown = false;
            } else if (feedback === AIUserFeedback.THUMBS_DOWN) {
              this.ratedThumbsDown = true;
              this.ratedThumbsUp = false;
            }
          },
          error: (error) => {
            this.error = `Failed to submit feedback: ${error}`;
          },
        });
    }
  }
}
